import ExpandIcon from '@mui/icons-material/KeyboardArrowRight';
import type {AccordionSummaryProps as MuiAccordionSummaryProps} from '@mui/material/AccordionSummary';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import {styled} from '@mui/material/styles';
import {forwardRef} from 'react';
import type {AccordionGlobalProps} from './Accordion';
import {excludeComponentProps} from '../utils/excludeComponentProps';

type OmittedAccordionSummaryProps = Omit<MuiAccordionSummaryProps, 'disableGutters' | 'centerRipple' | 'disableRipple' | 'disableTouchRipple' | 'focusRipple' | 'TouchRippleProps'>;
type OmittedAccordionProps = Omit<AccordionGlobalProps, 'variant' | 'children' | 'classes'>;

export interface AccordionSummaryProps extends OmittedAccordionSummaryProps, OmittedAccordionProps {}

const StyledAccordionSummary = styled(MuiAccordionSummary, {
    ...excludeComponentProps('rightAligned', 'generatedId', 'dense')
})<AccordionSummaryProps>(({
    theme, rightAligned, dense
}) => ({
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    paddingLeft: '0.5rem',
    paddingRight: '1rem',
    minHeight: '3.5rem',
    transition: 'all 300ms',
    ...dense && {
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem',
        paddingLeft: '0.25rem',
        paddingRight: '0.75rem',
        minHeight: undefined
    },
    ...rightAligned && {
        paddingLeft: '1.5rem',
        paddingRight: '0.5rem',

        ...dense && {
            paddingLeft: '1rem',
            paddingRight: '0.25rem'
        }
    },
    '& .MuiAccordionSummary-content': {
        margin: 0,
        order: 2,
        fontWeight: theme.tokens.FuelFontWeightBold,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',

        ...rightAligned && {
            order: 1
        }
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
        width: '2rem',
        height: '2rem',
        marginBlock: 0,

        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        order: 1,
        transform: 'rotate(0deg)',

        marginInline: '0rem 0.5rem',

        ...dense && {
            marginRight: '0.25rem'
        },

        ...rightAligned && {
            order: 2,
            transform: 'rotate(90deg)',

            marginInline: '0.5rem 0rem',

            ...dense && {
                marginInline: '0.25rem 0'
            }
        }
    },

    '&.Mui-focusVisible': {
        backgroundColor: 'inherit',
        '& .MuiAccordionSummary-expandIconWrapper': {
            borderRadius: theme.tokens.FuelBorderRadiusMd,
            boxShadow: theme.tokens.FuelShadowFocused
        }
    },

    '&.Mui-expanded': {
        margin: 0,

        '& .MuiAccordionSummary-content': {
            margin: 0
        },

        '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'rotate(90deg)',

            ...rightAligned && {
                transform: 'rotate(270deg)'
            }
        }
    },

    [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-start',

        '& .MuiAccordionSummary-expandIconWrapper': {
            flexGrow: 'none'
        },

        '& .MuiAccordionSummary-content': {
            flexWrap: 'wrap'
        }
    }
}));

export const AccordionSummary = forwardRef<HTMLDivElement, AccordionSummaryProps>(({
    generatedId, expandIcon = <ExpandIcon/>, rightAligned, children, ...props
}, ref) => (
    <StyledAccordionSummary
        expandIcon={expandIcon}
        ref={ref}
        rightAligned={rightAligned}
        {...props}
        aria-controls={`${generatedId}-itemDetails`}
        id={`${generatedId}-itemSummary`}
    >
        {children}
    </StyledAccordionSummary>
));

AccordionSummary.displayName = 'AccordionSummary';
